import classnames from 'classnames';
import * as React from 'react';
import useStartBookingFlow from 'booking/hooks/useStartBookingFlow';
import moment from 'lib/momentTimezone';
import { DayOfTheWeek } from '@bondvet/types/common';
import { formatTime } from 'lib/util';
import { OpeningHours } from 'lib/types';
import * as styles from './BookingBanner.module.scss';
import CallToAction from '../CallToAction';

type BookingBannerProps = {
    position?: 'top' | 'bottom';
    locationId: string;
    locationName: string;
    locationShortSlug: string;
    citySlug: string;
    openingHours: Partial<OpeningHours>;
    visible: boolean;
};

export default function BookingBanner({
    position = 'bottom',
    locationId,
    locationName,
    locationShortSlug,
    citySlug,
    openingHours,
    visible,
}: BookingBannerProps): React.ReactElement {
    const startBooking = useStartBookingFlow();
    const handleSubmit = () => {
        startBooking({
            locationId,
            locationSlug: locationShortSlug,
            citySlug,
        });
    };

    const closingHour = React.useMemo(() => {
        const day = moment().format('dddd').toLowerCase() as DayOfTheWeek;
        const hoursOfToday = openingHours[day];

        const time = hoursOfToday?.[hoursOfToday.length - 1]?.end;
        if (time) {
            return formatTime(time).toLowerCase();
        }
        return null;
    }, [openingHours]);

    return (
        <div
            className={classnames(styles.root, {
                [styles.topPosition]: position === 'top',
                [styles.bottomPosition]: position === 'bottom',
                [styles.visible]: visible,
            })}
        >
            <div className={styles.text}>
                <div className={styles.title}>
                    <h4>{locationName}</h4>
                </div>
                <div className={styles.subTitle}>
                    {closingHour
                        ? `Open until ${closingHour} today`
                        : 'Closed for the day'}
                </div>
            </div>
            <CallToAction
                variant="outlined"
                type="button"
                className={styles.cta}
                onClick={handleSubmit}
            >
                Book Now
            </CallToAction>
        </div>
    );
}
